import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, CheckPicker, Input, Loader, Message, Panel, SelectPicker, Tag } from 'rsuite';
import DatePicker from '../components/shared/DatePicker';
import { Button as NsiButton, ConfirmModal, Modal, useLastPresent } from '@nsi-advisory-services/components';
import {
  useDeleteRndCreditRun,
  useClearRndCache,
  useEmployees,
  useGetRndCreditRun,
  useReviewBucketEntry,
  useReviewRndEligibility,
  useRndCreditRuns,
  useRndCreditSettings,
  useRndCreditTasks,
  useRndPullStream,
  useSaveRndCreditRun,
  useUpdateRndCreditRun,
  useUpdateRndCreditSettings,
} from '../hooks/useQueries';
import { toDateOnlyString } from '../utils/payPeriods';
import { showToast, dismissToast } from '../utils/toast';
import TaskDndBoard from '../components/rndCredits/TaskDndBoard';
import { useRowFilter, RowFilterBar, useSortedRows, SortHeader } from '../components/rndCredits/RowFilter';
import ReviewCarousel from '../components/rndCredits/ReviewCarousel';
import PiePanel from '../components/rndCredits/PiePanel';
import PageHeader from '../components/shared/PageHeader';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

// Cache metadata for the clear-cache danger zone (label + irreversible warning).
const CACHE_META = {
  eligibility: {
    label: 'AI eligibility cache',
    warn: 'Every slush description will be re-screened for R&D eligibility (four-part test) on the next run — slower and it incurs API cost. Manual credit/no-credit decisions are NOT affected.',
  },
  classification: {
    label: 'AI bucket cache',
    warn: 'Every time-entry description will be re-sent to Azure OpenAI on the next run — slower and it incurs API cost. Manual bucket overrides are NOT affected.',
  },
  overrides: {
    label: 'Bucket overrides',
    warn: 'Every manual re-bucketing decision you have ever made will be permanently deleted. Those entries revert to the AI classification (often Uncategorized) and must be re-bucketed by hand.',
  },
  slush: {
    label: 'Slush review decisions',
    warn: 'Every saved credit / no-credit decision will be permanently deleted. All qualifying slush entries will return to the review queue and must be re-reviewed.',
  },
};

// Cache keys in display order for the toggle/clear lists.
const CACHE_KEYS = ['eligibility', 'classification', 'overrides', 'slush'];

// --- Quick-pick range helpers -------------------------------------------------
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const endOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0);

const buildQuickRanges = (now = new Date()) => {
  const prevQuarterRef = new Date(startOfQuarter(now));
  prevQuarterRef.setDate(0);
  const year = now.getFullYear();
  return [
    { key: 'this_quarter', label: 'This Quarter', start: startOfQuarter(now), end: endOfQuarter(now) },
    { key: 'last_quarter', label: 'Last Quarter', start: startOfQuarter(prevQuarterRef), end: endOfQuarter(prevQuarterRef) },
    { key: 'this_year', label: 'This Year', start: new Date(year, 0, 1), end: new Date(year, 11, 31) },
    { key: 'last_year', label: 'Last Year', start: new Date(year - 1, 0, 1), end: new Date(year - 1, 11, 31) },
    { key: 'ytd', label: 'Year to Date', start: new Date(year, 0, 1), end: now },
  ];
};

const EMPTY_ORDER = { all: [], slush: [], exclude: [] };

// "Apr 1 – Jun 30, 2026" (drops the year on the start when both share a year).
const formatRange = (start, end) => {
  const s = start ? new Date(`${start}T00:00:00`) : null;
  const e = end ? new Date(`${end}T00:00:00`) : null;
  const valid = (d) => d && !Number.isNaN(d.getTime());
  if (!valid(s) || !valid(e)) return `${start || '?'} → ${end || '?'}`;
  const sameYear = s.getFullYear() === e.getFullYear();
  const sTxt = s.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  const eTxt = e.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${sTxt} – ${eTxt}`;
};

const fmtHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round((n + Number.EPSILON) * 100) / 100);
};

// Currency (cost-to-company). Renders "—" when there's no value.
const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const taskWithParent = (taskName, parentTaskName) => {
  const task = String(taskName || '').trim() || 'Unknown task';
  const parent = String(parentTaskName || '').trim();
  return parent ? `${task} (Parent: ${parent})` : task;
};

const SummaryStat = ({ label, value, sub, style, valueStyle }) => (
  <Panel bordered bodyFill className="p-3" style={{ flex: '1 1 180px', minWidth: 180, ...style }}>
    <div className="text-muted small">{label}</div>
    <div style={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2, ...valueStyle }}>{value}</div>
    {sub ? <div className="text-muted small">{sub}</div> : null}
  </Panel>
);

const eligibilityTag = (level) => {
  if (level === 'all') return <Tag color="green" size="sm">All credit</Tag>;
  if (level === 'slush') return <Tag color="orange" size="sm">Slush</Tag>;
  return <Tag size="sm">Excluded</Tag>;
};

// AI R&D-eligibility verdict for an included entry (advisory for all-credit).
const eligibilityVerdictTag = (d) => {
  const conf = d.eligibilityConfidence == null ? null : `${fmtHours(d.eligibilityConfidence)}%`;
  if (d.eligibilitySource === 'human') {
    return <Tag color={d.eligible ? 'green' : 'red'} size="sm">{d.eligible ? 'You: eligible' : 'You: not eligible'}</Tag>;
  }
  // No description to screen (empty entry) — no meaningful AI verdict.
  if (d.eligibilitySource === 'empty' || (!d.eligible && d.eligibilityConfidence == null)) {
    return <Tag size="sm" className="text-muted">Not screened</Tag>;
  }
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <Tag color={d.eligible ? 'green' : 'red'} size="sm">AI: {d.eligible ? 'Eligible' : 'Not eligible'}</Tag>
      {conf ? <span className="text-muted small">{conf}</span> : null}
    </span>
  );
};

const WIZARD_STEPS = ['Range & Employees', 'Categorize Tasks', 'Review Eligibility', 'Review Buckets', 'Results'];
const STEP_ACCENT = '#169a8e';

function Stepper({ current }) {
  return (
    <div className="d-flex align-items-center flex-wrap" style={{ rowGap: 8 }}>
      {WIZARD_STEPS.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'todo';
        const color = state === 'todo' ? '#c0c0c0' : STEP_ACCENT;
        return (
          <React.Fragment key={label}>
            <div className="d-flex flex-column align-items-center" style={{ gap: 6, flex: '0 0 auto' }}>
              <div
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: state === 'todo' ? 'transparent' : color,
                  color: state === 'todo' ? 'var(--dm-text-faint, #999)' : '#fff',
                  border: `2px solid ${color}`, fontSize: 13, fontWeight: 600, flex: '0 0 auto',
                }}
              >
                {state === 'done' ? <i className="bi bi-check-lg" /> : i + 1}
              </div>
              <span style={{ color: state === 'active' ? 'var(--dm-text, #111)' : 'var(--dm-text-faint, #888)', fontWeight: state === 'active' ? 600 : 400, whiteSpace: 'nowrap', fontSize: 13, textAlign: 'center' }}>
                {label}
              </span>
            </div>
            {i < WIZARD_STEPS.length - 1 ? (
              <div style={{ flex: '1 1 24px', height: 2, background: 'var(--dm-surface-sunk, #e0e0e0)', margin: '0 12px', minWidth: 20, alignSelf: 'flex-start', marginTop: 13 }} />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const PHASE_LABEL = { pull: 'Pulling TimeCamp data…', classify: 'Classifying with Azure AI…', aggregate: 'Aggregating results…' };

// Build the DnD board's initial columns from tasks + saved eligibility/order.
const buildColumns = (tasks, savedElig = {}, savedOrder = {}) => {
  const levelOf = (id) => {
    const l = savedElig[id];
    return l === 'all' || l === 'slush' ? l : 'exclude';
  };
  const inCol = { all: [], slush: [], exclude: [] };
  tasks.forEach((t) => inCol[levelOf(t.taskId)].push(t.taskId));

  const hoursRank = new Map(tasks.map((t, i) => [t.taskId, i])); // tasks come hours-desc
  const cols = { all: [], slush: [], exclude: [] };
  ['all', 'slush', 'exclude'].forEach((col) => {
    const present = new Set(inCol[col]);
    const ordered = [];
    (savedOrder[col] || []).forEach((id) => {
      if (present.has(id)) { ordered.push(id); present.delete(id); }
    });
    const remaining = [...present].sort((a, b) => (hoursRank.get(a) ?? 0) - (hoursRank.get(b) ?? 0));
    cols[col] = [...ordered, ...remaining];
  });
  return cols;
};

export default function RndCreditsPage() {

  const employeesQuery = useEmployees({ enabled: true });
  const employeeOptions = useMemo(
    () =>
      (employeesQuery.data || [])
        .filter((emp) => emp?.is_active !== false)
        .map((emp) => ({
          label: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || 'Unknown',
          value: emp.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [employeesQuery.data]
  );

  const quickRanges = useMemo(() => buildQuickRanges(), []);

  const settingsQuery = useRndCreditSettings();
  const updateSettings = useUpdateRndCreditSettings();
  const pullTasks = useRndCreditTasks();
  const streamPull = useRndPullStream();
  const reviewEligibility = useReviewRndEligibility();
  const reviewBucket = useReviewBucketEntry();
  const clearCache = useClearRndCache();
  const runsQuery = useRndCreditRuns();
  const getRun = useGetRndCreditRun();
  const saveRun = useSaveRndCreditRun();
  const updateRun = useUpdateRndCreditRun();
  const deleteRun = useDeleteRndCreditRun();

  const availableBuckets = settingsQuery.data?.availableBuckets || [];
  const currentConfig = settingsQuery.data?.config || {};
  const countedBuckets = currentConfig.countedBuckets || [];

  const [view, setView] = useState('home'); // 'home' | 'wizard'
  const [runId, setRunId] = useState(null);
  const [runName, setRunName] = useState('');
  const [step, setStep] = useState(0);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [activeQuickRange, setActiveQuickRange] = useState(null);

  const [tasksData, setTasksData] = useState(null);
  const [columns, setColumns] = useState({ all: [], slush: [], exclude: [] });
  const [boardKey, setBoardKey] = useState(0);
  const [result, setResult] = useState(null);

  // Streaming run state.
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // { phase, done, total }
  const [runLogs, setRunLogs] = useState([]);

  // Live slush review lists.
  const [approved, setApproved] = useState([]);
  const [dropped, setDropped] = useState([]);
  const [pendingRefresh, setPendingRefresh] = useState(0);
  const [lastDecision, setLastDecision] = useState(null); // { entry, decision } — most recent, for banner undo
  // Slush review is cursor-based over a STABLE ordered list: the card is
  // reviewItems[reviewIndex]; deciding advances the cursor, "Back" moves it
  // back one (order never changes) and lets you re-pick. reviewDecisions maps
  // reviewKey -> 'include' | 'exclude' so a revisited card shows its choice.
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewDecisions, setReviewDecisions] = useState({});
  // Bucket review uses the same cursor model. reviewKey -> assigned bucket.
  const [bucketItems, setBucketItems] = useState([]);
  const [bucketIndex, setBucketIndex] = useState(0);
  const [bucketDecisions, setBucketDecisions] = useState({});
  const [logOpen, setLogOpen] = useState(false);
  const [runToDelete, setRunToDelete] = useState(null); // saved run pending delete confirmation
  const [cacheToClear, setCacheToClear] = useState(null); // cache key pending clear confirmation
  const [renameTarget, setRenameTarget] = useState(null); // { id, name, isCurrent } pending rename
  const [renameValue, setRenameValue] = useState('');
  const [detailEntry, setDetailEntry] = useState(null); // included-entry row opened in the audit modal
  const detailShown = useLastPresent(detailEntry); // keeps the dialog steady while it fades out
  // Descriptions denied from the results audit this session (optimistic strike);
  // reconciled by "Refresh finals". Deny is description-keyed, so it marks every
  // included row sharing the description.
  const [deniedDescriptions, setDeniedDescriptions] = useState(() => new Set());
  const logBodyRef = useRef(null);
  const pullAbortRef = useRef(null);
  const summaryRef = useRef(null); // KPI tiles row — captured into the PDF
  const chartsRef = useRef(null);  // pie charts row — captured into the PDF
  const [exporting, setExporting] = useState(false); // forces pies to chart view during capture

  // Per-run cache toggles (default: use all four). Turning one off redoes that
  // cached work from scratch on the next run.
  const [caches, setCaches] = useState({ eligibility: true, classification: true, overrides: true, slush: true });

  // Cross-table filters (employee dropdown + click-through from the pies).
  const [tableEmployee, setTableEmployee] = useState(null);
  const [tableTask, setTableTask] = useState(null);
  const [tableBucket, setTableBucket] = useState(null);
  // Included-table only: filter by the AI R&D-eligibility verdict.
  // null = all · 'eligible' = AI eligible · 'not_eligible' = AI not eligible.
  const [includedEligFilter, setIncludedEligFilter] = useState(null);
  const passesShared = (r) =>
    (!tableEmployee || r.user === tableEmployee) &&
    (!tableTask || taskWithParent(r.taskName, r.parentTaskName) === tableTask) &&
    (!tableBucket || r.bucket === tableBucket);
  const preRows = useMemo(() => (result?.rows || []).filter(passesShared), [result, tableEmployee, tableTask, tableBucket]); // eslint-disable-line react-hooks/exhaustive-deps
  const preIncluded = useMemo(
    () => (result?.included || []).filter(passesShared).filter((r) => {
      if (includedEligFilter === 'eligible') return r.eligible === true;
      if (includedEligFilter === 'not_eligible') return r.eligible === false;
      return true;
    }),
    [result, tableEmployee, tableTask, tableBucket, includedEligFilter] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const preApproved = useMemo(() => approved.filter(passesShared), [approved, tableEmployee, tableTask, tableBucket]); // eslint-disable-line react-hooks/exhaustive-deps
  const preDropped = useMemo(() => dropped.filter(passesShared), [dropped, tableEmployee, tableTask, tableBucket]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter/search state for the result tables (search + task/bucket per table).
  const rowsFilter = useRowFilter(preRows, { searchFields: ['year', 'user', 'taskName', 'parentTaskName', 'bucket'] });
  const includedFilter = useRowFilter(preIncluded, { searchFields: ['year', 'user', 'taskName', 'parentTaskName', 'bucket', 'description'] });
  const approvedFilter = useRowFilter(preApproved, { searchFields: ['user', 'taskName', 'parentTaskName', 'description', 'bucket'] });
  const droppedFilter = useRowFilter(preDropped, { searchFields: ['user', 'taskName', 'parentTaskName', 'description', 'bucket', 'reason'] });

  // Header-click sorting for each result table (applied after filtering).
  const rowsSort = useSortedRows(rowsFilter.filtered);
  const includedSort = useSortedRows(includedFilter.filtered);
  const approvedSort = useSortedRows(approvedFilter.filtered);
  const droppedSort = useSortedRows(droppedFilter.filtered);

  // Sorting for the By-Bucket / By-Employee / By-Task summary tables (pie panels).
  const byBucketSort = useSortedRows(result?.byBucket || []);
  const byUserSort = useSortedRows(result?.byUser || []);
  const byTaskDisplayRows = useMemo(
    () => (result?.byTask || []).map((t) => ({ ...t, taskDisplay: taskWithParent(t.taskName, t.parentTaskName) })),
    [result]
  );
  const byTaskSort = useSortedRows(byTaskDisplayRows);

  const employeeFilterOptions = useMemo(
    () => (result?.byUser || []).map((u) => ({ label: u.user, value: u.user })),
    [result]
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftCounted, setDraftCounted] = useState([]);
  const [draftDescriptions, setDraftDescriptions] = useState({});
  const [draftBucketPrompt, setDraftBucketPrompt] = useState('');
  const [draftEligPrompt, setDraftEligPrompt] = useState('');
  const defaultBucketPrompt = settingsQuery.data?.defaultClassifierPrompt || '';
  const defaultEligibilityPrompt = settingsQuery.data?.defaultEligibilityPrompt || '';

  const tasksById = useMemo(() => {
    const map = {};
    (tasksData?.tasks || []).forEach((t) => { map[t.taskId] = t; });
    return map;
  }, [tasksData]);

  useEffect(() => {
    if (!settingsOpen) return;
    setDraftCounted(currentConfig.countedBuckets || availableBuckets.map((b) => b.key));
    const seed = {};
    availableBuckets.forEach((b) => { seed[b.key] = currentConfig.bucketDescriptions?.[b.key] ?? b.description; });
    setDraftDescriptions(seed);
    // Blank saved prompt => show the default so the admin edits the real thing.
    const savedBucket = currentConfig.bucketPrompt ?? currentConfig.classifierPrompt; // legacy fallback
    setDraftBucketPrompt(savedBucket?.trim() ? savedBucket : defaultBucketPrompt);
    setDraftEligPrompt(currentConfig.eligibilityPrompt?.trim() ? currentConfig.eligibilityPrompt : defaultEligibilityPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsOpen]);

  useEffect(() => {
    if (!result) {
      setApproved([]); setDropped([]);
      setReviewItems([]); setReviewIndex(0); setReviewDecisions({});
      setBucketItems([]); setBucketIndex(0); setBucketDecisions({});
      setPendingRefresh(0); setLastDecision(null);
      setTableEmployee(null); setTableTask(null); setTableBucket(null);
      setDeniedDescriptions(new Set());
      return;
    }
    // Eligibility phase (Step 1 gate): populate ONLY the slush review carousel;
    // leave bucket/results state untouched (they come from the full phase).
    if (result.phase === 'eligibility') {
      const items = result.reviewQueue || [];
      setReviewItems(items);
      setReviewIndex(0);
      const seed = {};
      items.forEach((it) => { if (it.decision) seed[it.reviewKey] = it.decision; });
      setReviewDecisions(seed);
      return;
    }
    // Full phase (or a recalled saved run): populate bucket + results state.
    if (result.status === 'ok') {
      setApproved(result.approvedSlush || []);
      setDropped(result.droppedSlush || []);
      setBucketItems(result.bucketReviewQueue || []);
      setBucketIndex(0);
      setBucketDecisions({});
      setPendingRefresh(0);
      setLastDecision(null);
      setTableEmployee(null);
      setTableTask(null);
      setTableBucket(null);
      setDeniedDescriptions(new Set());
    }
  }, [result]);

  // Slush entries still awaiting a decision (drives the results "Unreviewed"
  // panel and the empty/summary state), preserving stable order.
  const reviewRemaining = useMemo(
    () => reviewItems.filter((it) => !reviewDecisions[it.reviewKey]),
    [reviewItems, reviewDecisions]
  );

  // Back one card in the slush review (cursor only — does not touch decisions).
  const slushBack = () => setReviewIndex((i) => Math.max(0, i - 1));

  const applyQuickRange = (range) => {
    setStartDate(range.start);
    setEndDate(range.end);
    setActiveQuickRange(range.key);
  };
  const handleStartChange = (value) => {
    setStartDate(value);
    setActiveQuickRange(null);
    if (value && endDate && endDate < value) setEndDate(null);
  };
  const handleEndChange = (value) => {
    setEndDate(value);
    setActiveQuickRange(null);
  };

  const startIso = startDate ? toDateOnlyString(startDate) : '';
  const endIso = endDate ? toDateOnlyString(endDate) : '';
  const rangeValid = Boolean(startIso && endIso && startIso <= endIso);

  const selectedEmployeeLabel =
    selectedEmployeeIds.length === 0
      ? 'All employees'
      : `${selectedEmployeeIds.length} employee${selectedEmployeeIds.length === 1 ? '' : 's'}`;

  // Merge overrides onto the full saved config so a partial save never wipes a field.
  const mergedConfig = (over) => ({
    countedBuckets: currentConfig.countedBuckets || countedBuckets,
    taskEligibility: currentConfig.taskEligibility || {},
    bucketDescriptions: currentConfig.bucketDescriptions || {},
    taskOrder: currentConfig.taskOrder || EMPTY_ORDER,
    bucketPrompt: currentConfig.bucketPrompt ?? currentConfig.classifierPrompt ?? '',
    eligibilityPrompt: currentConfig.eligibilityPrompt || '',
    ...over,
  });

  const eligibilityFromColumns = () => {
    const map = {};
    (columns.all || []).forEach((id) => { map[id] = 'all'; });
    (columns.slush || []).forEach((id) => { map[id] = 'slush'; });
    return map;
  };

  const handlePullTasks = async () => {
    if (!rangeValid) return;
    setResult(null);
    try {
      const data = await pullTasks.mutateAsync({ start: startIso, end: endIso, employeeIds: selectedEmployeeIds });
      setTasksData(data);
      setColumns(buildColumns(data.tasks || [], data.savedEligibility || {}, currentConfig.taskOrder || EMPTY_ORDER));
      setBoardKey((k) => k + 1);
      setStep(1);
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not pull tasks.');
    }
  };

  const setAllTasksTo = (col) => {
    const allIds = (tasksData?.tasks || []).map((t) => t.taskId);
    const next = { all: [], slush: [], exclude: [] };
    next[col] = allIds;
    setColumns(next);
    setBoardKey((k) => k + 1);
  };

  const eligibilityCounts = {
    all: (columns.all || []).length,
    slush: (columns.slush || []).length,
    exclude: (columns.exclude || []).length,
  };

  // `phase` splits the run: 'eligibility' screens slush (Review Slush step) and
  // 'full' buckets the survivors + aggregates. Re-pulls between steps use all
  // caches so they reflect just-saved decisions; only the initial run honors the
  // user's per-run cache toggles.
  const runPull = async (runCaches = { eligibility: true, classification: true, overrides: true }, phase = 'full') => {
    const controller = new AbortController();
    pullAbortRef.current = controller;
    setRunning(true);
    setResult(null);
    setRunLogs([]);
    setProgress(null);
    try {
      const data = await streamPull(
        { start: startIso, end: endIso, employeeIds: selectedEmployeeIds, caches: runCaches, phase },
        {
          onLog: (message) => setRunLogs((prev) => [...prev, message]),
          onProgress: (p) => setProgress(p),
          signal: controller.signal,
        }
      );
      setResult(data);
      return data;
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) {
        showToast('info', 'Run cancelled. Any completed classifications were saved.');
        return null;
      }
      throw err;
    } finally {
      pullAbortRef.current = null;
      setRunning(false);
      setProgress(null);
    }
  };

  const cancelRun = () => {
    if (pullAbortRef.current) {
      setRunLogs((prev) => [...prev, 'Cancelling run…']);
      pullAbortRef.current.abort();
    }
  };

  // From Categorize Tasks: save eligibility, then run ONLY the slush eligibility
  // screen (Step 1) and advance to Review Slush. Bucketing is deferred until the
  // user has reviewed slush, so no-credit items never hit the bucket classifier.
  const handleRun = async () => {
    if (eligibilityCounts.all + eligibilityCounts.slush === 0) {
      showToast('warning', 'Put at least one task in Full credit or Slush first.');
      return;
    }
    try {
      await updateSettings.mutateAsync(mergedConfig({ taskEligibility: eligibilityFromColumns(), taskOrder: columns }));
      setStep(2); // Review Slush — loading UI shows here while eligibility screens
      const data = await runPull(caches, 'eligibility');
      if (data?.status === 'classifier_unavailable') showToast('info', data.message || 'Classifier unavailable.');
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not screen eligibility.');
    }
  };

  // --- Saved runs -----------------------------------------------------------
  const defaultRunName = () =>
    `${startIso} → ${endIso}${selectedEmployeeIds.length ? ` · ${selectedEmployeeIds.length} emp` : ' · all'}`;

  // Create (first time) or update the saved run snapshot for the current result.
  const persistRun = async (data) => {
    if (!data || data.status !== 'ok') return;
    try {
      if (runId) {
        await updateRun.mutateAsync({ id: runId, result: data });
      } else {
        const name = runName || defaultRunName();
        const resp = await saveRun.mutateAsync({ name, start: startIso, end: endIso, employeeIds: selectedEmployeeIds, result: data });
        if (resp?.id) { setRunId(resp.id); setRunName(name); }
      }
    } catch (err) {
      showToast('error', `Could not save run: ${String(err?.message || err)}`);
    }
  };

  const startNewRun = () => {
    setRunId(null); setRunName('');
    setResult(null); setTasksData(null); setRunLogs([]);
    setColumns({ all: [], slush: [], exclude: [] });
    setStartDate(null); setEndDate(null); setSelectedEmployeeIds([]); setActiveQuickRange(null);
    setStep(0);
    setView('wizard');
  };

  const recallRun = async (id) => {
    try {
      const run = await getRun(id);
      setRunId(run.id);
      setRunName(run.name || '');
      setResult(run.result);
      if (run.period_start) setStartDate(new Date(`${run.period_start}T00:00:00`));
      if (run.period_end) setEndDate(new Date(`${run.period_end}T00:00:00`));
      setSelectedEmployeeIds(Array.isArray(run.employee_ids) ? run.employee_ids : []);
      setActiveQuickRange(null);
      setTasksData(null);
      setRunLogs([]);
      setStep(4);
      setView('wizard');
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not load run.');
    }
  };

  const confirmDeleteRun = async () => {
    if (!runToDelete) return;
    try {
      await deleteRun.mutateAsync(runToDelete.id);
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not delete run.');
    }
    setRunToDelete(null);
  };

  // Open the rename dialog. `target` = { id, name, isCurrent }; id may be null for
  // a not-yet-saved current run (then we only set the local name for the first save).
  const openRename = (target) => { setRenameTarget(target); setRenameValue(target.name || ''); };
  const confirmRename = async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    try {
      if (renameTarget.id) await updateRun.mutateAsync({ id: renameTarget.id, name });
      if (renameTarget.isCurrent) setRunName(name);
      setRenameTarget(null);
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not rename run.');
    }
  };

  const refreshFinals = async () => {
    try { const data = await runPull(); await persistRun(data); } catch (err) { showToast('error', String(err?.message || err) || 'Could not refresh finals.'); }
  };

  // From Review Slush → Review Buckets: run the FULL pass, which buckets only the
  // all-credit + surviving (credited) slush. Eligibility uses its cache (already
  // screened in Step 1); bucket toggles still honor the user's choices.
  const goToBuckets = async () => {
    setStep(3);
    try {
      await runPull({ eligibility: true, classification: caches.classification, overrides: caches.overrides }, 'full');
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not classify buckets.');
    }
  };

  // Back to Review Slush — re-run the eligibility screen to repopulate the queue.
  const goToSlushReview = async () => {
    setStep(2);
    try {
      await runPull({ eligibility: true, classification: true, overrides: true }, 'eligibility');
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not load slush review.');
    }
  };

  // Prev button: going back to Review Slush must re-screen eligibility.
  const goPrev = () => {
    const target = Math.max(0, step - 1);
    if (target === 2) { goToSlushReview(); return; }
    setStep(target);
  };

  // Advance to Results, recomputing finals so re-buckets are counted.
  const goToResults = async () => {
    setStep(4);
    let data = result;
    if (pendingRefresh > 0) {
      try { data = await runPull(); } catch (err) { showToast('error', String(err?.message || err) || 'Could not refresh finals.'); return; }
    }
    await persistRun(data);
  };

  // Slush eligibility carousel decide: persist a description-keyed credit/no-credit
  // decision (gates bucketing), record it, then advance the cursor.
  const slushDecide = async (item, value) => {
    try {
      await reviewEligibility.mutateAsync({ decision: value, description: item.description });
      setReviewDecisions((prev) => ({ ...prev, [item.reviewKey]: value }));
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not save decision.');
    }
    setReviewIndex((i) => Math.min(i + 1, reviewItems.length));
  };

  // Bucket carousel: assign a bucket, or mark not eligible (description-keyed
  // eligibility cache override), then advance the cursor.
  const bucketDecide = async (item, value) => {
    if (value === '__not_eligible__') {
      try {
        await reviewEligibility.mutateAsync({ decision: 'exclude', description: item.description });
        setBucketDecisions((prev) => ({ ...prev, [item.reviewKey]: value }));
        setPendingRefresh((n) => n + 1);
        setBucketIndex((i) => Math.min(i + 1, bucketItems.length));
      } catch (err) {
        showToast('error', String(err?.message || err) || 'Could not mark as not eligible.');
      }
      return;
    }
    try {
      await reviewBucket.mutateAsync({ cacheKey: item.cacheKey, description: item.description, bucket: value });
      setBucketDecisions((prev) => ({ ...prev, [item.reviewKey]: value }));
      setPendingRefresh((n) => n + 1);
      setBucketIndex((i) => Math.min(i + 1, bucketItems.length));
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not save bucket.');
    }
  };

  // Back one card in the bucket review (cursor only — keeps prior assignment).
  const bucketBack = () => setBucketIndex((i) => Math.max(0, i - 1));

  const saveSettings = async () => {
    try {
      const descs = {};
      availableBuckets.forEach((b) => {
        const text = (draftDescriptions[b.key] || '').trim();
        if (text && text !== b.description) descs[b.key] = text;
      });
      // Store '' when a prompt is blank or unchanged from the default, so the
      // built-in default keeps applying (and future default edits propagate).
      const normalizePrompt = (draft, def) => {
        const text = (draft || '').trim();
        return text && text !== def.trim() ? text : '';
      };
      const bucketPrompt = normalizePrompt(draftBucketPrompt, defaultBucketPrompt);
      const eligibilityPrompt = normalizePrompt(draftEligPrompt, defaultEligibilityPrompt);
      await updateSettings.mutateAsync(mergedConfig({ countedBuckets: draftCounted, bucketDescriptions: descs, bucketPrompt, eligibilityPrompt }));
      showToast('success', 'R&D credit settings saved.');
      setSettingsOpen(false);
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not save settings.');
    }
  };

  const confirmClearCache = async () => {
    if (!cacheToClear) return;
    try {
      const resp = await clearCache.mutateAsync({ cache: cacheToClear });
      showToast('success', `Cleared ${CACHE_META[cacheToClear]?.label || cacheToClear} (${resp?.cleared ?? 0} record${resp?.cleared === 1 ? '' : 's'}).`);
      setCacheToClear(null);
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not clear cache.');
    }
  };

  const buildExportMeta = () => ({
    start: startIso,
    end: endIso,
    employeesLabel: selectedEmployeeLabel,
    countedBuckets,
    generatedAt: new Date().toLocaleString(),
  });

  // Rasterize a DOM node to a PNG data URL (used to embed the KPI cards + charts
  // into the PDF). Returns null on failure so export still proceeds text-only.
  const captureNode = async (node) => {
    if (!node) return null;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: 'var(--dm-surface, #ffffff)', logging: false, useCORS: true });
      return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
    } catch (err) {
      return null;
    }
  };

  const handleExport = async (format) => {
    try {
      let data = result;
      if (pendingRefresh > 0) data = await runPull();
      if (!data || data.status !== 'ok') { showToast('warning', 'Run the report before exporting.'); return; }
      const meta = buildExportMeta();
      const mod = await import('../utils/rndExport');
      if (format === 'pdf') {
        // Neutralize the view before snapshotting: clear cross-table filters and
        // force every pie into chart mode so the capture is clean.
        setTableEmployee(null);
        setTableTask(null);
        setTableBucket(null);
        setExporting(true);
        try {
          // Wait two paints so the state resets above are on screen, then snapshot.
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
          const [summaryImg, chartsImg] = await Promise.all([captureNode(summaryRef.current), captureNode(chartsRef.current)]);
          mod.exportRndPdf(data, meta, { summaryImg, chartsImg });
        } finally {
          setExporting(false);
        }
      } else {
        mod.exportRndExcel(data, meta);
      }
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Export failed.');
    }
  };

  const removeByKey = (list, reviewKey) => list.filter((d) => d.reviewKey !== reviewKey);
  // Results-audit credit/no-credit: persists a description-keyed eligibility
  // decision (same store as the Review Slush step). 'reset' reverts to the AI
  // verdict. Optimistically moves the clicked row; a Refresh reconciles the rest.
  const decide = async (entry, decision) => {
    try {
      await reviewEligibility.mutateAsync({ decision, description: entry.description });
      setApproved((prev) => removeByKey(prev, entry.reviewKey));
      setDropped((prev) => removeByKey(prev, entry.reviewKey));
      if (decision === 'include') setApproved((prev) => [...prev, { ...entry }]);
      else if (decision === 'exclude') setDropped((prev) => [...prev, { ...entry, reason: 'reviewed_excluded' }]);
      // Optimistically strike every included row sharing this description (deny is
      // description-keyed); "Refresh finals" reconciles the authoritative totals.
      setDeniedDescriptions((prev) => {
        const next = new Set(prev);
        if (decision === 'exclude') next.add(entry.description);
        else next.delete(entry.description);
        return next;
      });
      setReviewDecisions((prev) => {
        const next = { ...prev };
        if (decision === 'reset') delete next[entry.reviewKey];
        else next[entry.reviewKey] = decision;
        return next;
      });
      setPendingRefresh((n) => n + 1);
      setLastDecision(decision === 'reset' ? null : { entry, decision });
    } catch (err) {
      showToast('error', String(err?.message || err) || 'Could not save decision.');
    }
  };

  // Undo the most recent decision made from the results audit lists.
  const undoLastDecision = async () => {
    if (!lastDecision) return;
    await decide(lastDecision.entry, 'reset');
    setLastDecision(null);
  };

  // Credit a dropped entry and offer an inline Undo toast.
  const creditAnyway = async (entry) => {
    await decide(entry, 'include');
    let id;
    id = showToast(
      'success',
      (
        <span>
          Credited “{taskWithParent(entry.taskName, entry.parentTaskName)}”.{' '}
          <Button
            appearance="link"
            size="xs"
            className="p-0"
            onClick={() => { dismissToast(id); decide(entry, 'reset'); }}
          >
            Undo
          </Button>
        </span>
      ),
      { duration: 8000 }
    );
  };

  // Live logs: task-pull logs, then the streaming run logs (which include the
  // full pull play-by-play as it happens).
  const combinedLogs = [
    ...((tasksData && tasksData.logs) || []),
    ...runLogs,
  ];

  // Keep the floating log scrolled to the newest line while open.
  useEffect(() => {
    if (logOpen && logBodyRef.current) {
      logBodyRef.current.scrollTop = logBodyRef.current.scrollHeight;
    }
  }, [logOpen, combinedLogs.length]);

  const isClassifyPhase = progress && progress.phase === 'classify' && progress.total > 0;
  const progressPct = isClassifyPhase ? Math.round((progress.done / progress.total) * 100) : 100;
  const loadingPanel = (
    <Panel bordered>
      <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ padding: '48px 16px' }}>
        <Loader size="lg" />
        <div className="mt-3" style={{ fontSize: 16, fontWeight: 600 }}>
          {progress ? (PHASE_LABEL[progress.phase] || 'Working…') : 'Working…'}
        </div>
        <div className="text-muted small mt-1">This can take a bit on the first run while new descriptions are classified.</div>
        <div className="w-100 mt-4" style={{ maxWidth: 520 }}>
          <div className="progress" style={{ height: 20 }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${isClassifyPhase ? progressPct : 100}%`, backgroundColor: STEP_ACCENT }}
              aria-valuenow={isClassifyPhase ? progressPct : 100}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              {isClassifyPhase ? `${progress.done} / ${progress.total} classified (${progressPct}%)` : ''}
            </div>
          </div>
        </div>
        <Button color="red" appearance="primary" size="lg" className="mt-4" onClick={cancelRun}>
          <i className="bi bi-x-octagon me-2" /> Cancel run
        </Button>
        <div className="text-muted small mt-2">Cancelling stops the Azure batch job. Descriptions already classified are kept.</div>
      </div>
    </Panel>
  );

  const savedRuns = runsQuery.data?.runs || [];
  const homeView = (
    <>
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'R&D Credits' },
        ]}
        icon="bi-calculator"
        title="R&D Credits"
        actions={(
          <>
            <button type="button" className="bdc-btn" onClick={() => setSettingsOpen(true)}>
              <i className="bi bi-gear" aria-hidden="true" />
              Edit config
            </button>
            <button type="button" className="bdc-btn bdc-btn--primary" onClick={startNewRun}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              New run
            </button>
          </>
        )}
      />

      {runsQuery.isLoading ? (
        <div className="text-center py-5"><Loader size="md" content="Loading saved runs…" /></div>
      ) : savedRuns.length === 0 ? (
        <Message type="info" showIcon>No saved runs yet. Click <strong>New run</strong> to create one.</Message>
      ) : (
        <div className="row g-3">
          <style>{`
            .rnd-run-card{border:1px solid var(--bdc-primary,#007374);border-radius:12px;padding:16px;background:var(--dm-surface, #fff);height:100%;cursor:pointer;transition:transform 160ms ease,box-shadow 160ms ease;box-shadow:0 1px 2px rgba(0,0,0,0.05);display:flex;flex-direction:column;}
            .rnd-run-card:hover,.rnd-run-card:focus-visible{transform:translateY(-4px);box-shadow:0 12px 26px rgba(0,115,116,0.20);outline:none;}
            .rnd-run-icon{width:38px;height:38px;border-radius:50%;background:rgba(0,115,116,0.10);color:var(--bdc-primary,#007374);display:flex;align-items:center;justify-content:center;font-size:18px;flex:0 0 auto;}
            .rnd-run-stat{font-size:1.5rem;font-weight:700;line-height:1.05;font-variant-numeric:tabular-nums;}
            .rnd-run-open{color:var(--bdc-primary,#007374);font-weight:600;}
          `}</style>
          {savedRuns.map((run) => {
            const ids = Array.isArray(run.employee_ids) ? run.employee_ids : [];
            const scope = ids.length === 0 ? 'All employees' : `${ids.length} employee${ids.length === 1 ? '' : 's'}`;
            const rangeText = formatRange(run.period_start, run.period_end);
            const isAutoName = !run.name || (run.period_start && String(run.name).includes(run.period_start));
            const title = isAutoName ? rangeText : run.name;
            const subtitle = isAutoName ? scope : `${rangeText} · ${scope}`;
            return (
              <div className="col-12 col-md-6 col-lg-4" key={run.id}>
                <div
                  className="rnd-run-card"
                  onClick={() => recallRun(run.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') recallRun(run.id); }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                      <div className="rnd-run-icon"><i className="bi bi-clock-history" /></div>
                      <div style={{ minWidth: 0 }}>
                        <div className="text-truncate" style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }} title={title}>{title}</div>
                        <div className="text-muted small text-truncate" title={subtitle}>{subtitle}</div>
                      </div>
                    </div>
                    <div className="d-flex gap-1" style={{ flex: '0 0 auto' }}>
                      <Button
                        size="xs"
                        appearance="subtle"
                        title="Rename run"
                        onClick={(e) => { e.stopPropagation(); openRename({ id: run.id, name: run.name || '', isCurrent: run.id === runId }); }}
                      >
                        <i className="bi bi-pencil" />
                      </Button>
                      <Button
                        size="xs"
                        appearance="subtle"
                        color="red"
                        title="Delete run"
                        onClick={(e) => { e.stopPropagation(); setRunToDelete(run); }}
                      >
                        <i className="bi bi-trash" />
                      </Button>
                    </div>
                  </div>

                  <div className="d-flex gap-4 mt-3">
                    <div>
                      <div className="rnd-run-stat">{fmtHours(run.total_hours)} <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>h</span></div>
                      <div className="text-muted small">R&amp;D hours</div>
                    </div>
                    <div>
                      <div className="rnd-run-stat">{run.included_entries ?? '—'}</div>
                      <div className="text-muted small">entries</div>
                    </div>
                    {run.pending_review ? (
                      <div>
                        <div className="rnd-run-stat" style={{ color: 'var(--dm-text-amber, #ef6c00)' }}>{run.pending_review}</div>
                        <div className="text-muted small">pending</div>
                      </div>
                    ) : null}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-auto pt-3">
                    <span className="text-muted small">Saved {new Date(run.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span className="rnd-run-open small">Open <i className="bi bi-arrow-right ms-1" /></span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const wizardView = (
    <>
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'R&D Credits', to: '/admin/rd-credits' },
          { label: runName || 'Untitled run' },
        ]}
        icon="bi-calculator"
        title={runName || 'R&D Credits'}
        backLabel="Back to runs"
        onBack={() => setView('home')}
        actions={(
          <>
            <button
              type="button"
              className="bdc-btn"
              title={runName ? 'Rename run' : 'Name this run'}
              onClick={() => openRename({ id: runId, name: runName, isCurrent: true })}
            >
              <i className="bi bi-pencil" aria-hidden="true" />
              {runName ? 'Rename' : 'Name run'}
            </button>
            <button type="button" className="bdc-btn" onClick={() => setSettingsOpen(true)}>
              <i className="bi bi-gear" aria-hidden="true" />
              Edit config
            </button>
          </>
        )}
      />

      <Panel bordered bodyFill className="px-3 py-2">
        <Stepper current={step} />
      </Panel>

      {/* Step 1 — range + employees */}
      {step === 0 ? (
        <Panel bordered header="Select range & employees">
          <div className="d-flex flex-column gap-3">
            <div>
              <div className="text-muted small mb-1">Quick ranges</div>
              <div className="d-flex flex-wrap gap-2">
                {quickRanges.map((range) => (
                  <Button
                    key={range.key}
                    size="sm"
                    appearance={activeQuickRange === range.key ? 'primary' : 'default'}
                    onClick={() => applyQuickRange(range)}
                  >
                    {range.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-3 align-items-end">
              <div style={{ minWidth: 180 }}>
                <div className="text-muted small mb-1">Start date</div>
                <DatePicker value={startDate} onChange={handleStartChange} oneTap format="MM/dd/yyyy" placeholder="Start date" cleanable={false} block />
              </div>
              <div style={{ minWidth: 180 }}>
                <div className="text-muted small mb-1">End date</div>
                <DatePicker value={endDate} onChange={handleEndChange} oneTap format="MM/dd/yyyy" placeholder="End date" cleanable={false} block shouldDisableDate={(date) => (startDate ? date < startDate : false)} />
              </div>
              <div style={{ minWidth: 280, flex: '1 1 280px' }}>
                <div className="text-muted small mb-1">Employees</div>
                <CheckPicker
                  data={employeeOptions}
                  countable={false}
                  renderValue={renderEmployeeSelection(employeeOptions)}
                  value={selectedEmployeeIds}
                  onChange={(value) => setSelectedEmployeeIds(value || [])}
                  placeholder={employeesQuery.isLoading ? 'Loading…' : 'All employees'}
                  loading={employeesQuery.isLoading}
                  block
                  sticky
                  renderExtraFooter={() => (
                    <div className="d-flex justify-content-between p-2 border-top">
                      <Button appearance="link" size="xs" onClick={() => setSelectedEmployeeIds(employeeOptions.map((o) => o.value))}>Select all</Button>
                      <Button appearance="link" size="xs" onClick={() => setSelectedEmployeeIds([])}>Clear</Button>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </Panel>
      ) : null}

      {/* Step 2 — task categorization board */}
      {step === 1 && tasksData ? (
        <Panel bordered header="Categorize tasks — drag each into a bucket">
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <span className="text-muted small">
              {tasksData.tasks.length} tasks · {eligibilityCounts.all} full-credit · {eligibilityCounts.slush} slush · {eligibilityCounts.exclude} excluded
            </span>
            <span className="ms-auto d-flex gap-2">
              <Button size="xs" appearance="ghost" onClick={() => setAllTasksTo('all')}>All → Full credit</Button>
              <Button size="xs" appearance="ghost" onClick={() => setAllTasksTo('slush')}>All → Slush</Button>
              <Button size="xs" appearance="ghost" onClick={() => setAllTasksTo('exclude')}>All → Excluded</Button>
            </span>
          </div>

          <TaskDndBoard key={boardKey} initialColumns={columns} tasksById={tasksById} onChange={setColumns} />

          <div className="text-muted small mt-3">
            Counted buckets: {countedBuckets.length ? countedBuckets.join(', ') : 'None'} · slush entries count only if the AI classifies them into one of these (with review). Edit descriptions via <strong>Edit config</strong>.
          </div>

          <div className="mt-3 p-3 rounded" style={{ background: 'rgba(0,115,116,0.05)' }}>
            <div className="fw-semibold small mb-1">Cache use for this run</div>
            <div className="text-muted small mb-2">Uncheck a cache to redo that work from scratch. Cached results are still re-saved afterward.</div>
            <div className="d-flex flex-wrap gap-3">
              <Checkbox checked={caches.eligibility} onChange={(_v, checked) => setCaches((c) => ({ ...c, eligibility: checked }))}>
                AI eligibility <span className="text-muted">(re-screen slush)</span>
              </Checkbox>
              <Checkbox checked={caches.classification} onChange={(_v, checked) => setCaches((c) => ({ ...c, classification: checked }))}>
                AI buckets <span className="text-muted">(re-run the AI)</span>
              </Checkbox>
              <Checkbox checked={caches.overrides} onChange={(_v, checked) => setCaches((c) => ({ ...c, overrides: checked }))}>
                Bucket overrides <span className="text-muted">(re-do manual re-buckets)</span>
              </Checkbox>
              <Checkbox checked={caches.slush} onChange={(_v, checked) => setCaches((c) => ({ ...c, slush: checked }))}>
                Slush decisions <span className="text-muted">(re-review credit/no-credit)</span>
              </Checkbox>
            </div>
          </div>
        </Panel>
      ) : null}

      {/* Step 4 — review buckets (re-bucket the Uncategorized) */}
      {step === 3 ? (
        running ? loadingPanel
          : result && result.status === 'classifier_unavailable' ? (
            <Message type="warning" showIcon>{result.message}</Message>
          ) : result && result.status === 'ok' ? (
            <Panel bordered header={bucketIndex < bucketItems.length ? `Review buckets — ${bucketIndex + 1} of ${bucketItems.length}` : 'Review buckets'}>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {(result.bucketDistribution || []).map((b) => (
                  <div key={b.bucket} className="border rounded px-3 py-2" style={{ minWidth: 160, background: b.bucket === 'Uncategorized' ? 'rgba(239,108,0,0.06)' : 'var(--dm-surface, #fafafa)' }}>
                    <div style={{ fontWeight: 600 }}>{b.bucket}</div>
                    <div className="text-muted small">{b.entries} entries · {b.taskCount} task{b.taskCount === 1 ? '' : 's'} · {fmtHours(b.hours)} h</div>
                  </div>
                ))}
              </div>

              {bucketIndex >= bucketItems.length ? (
                <div className="text-center" style={{ padding: '32px 16px' }}>
                  <i className={`bi ${bucketItems.length === 0 ? 'bi-clipboard-check' : 'bi-check-circle-fill'} text-success`} style={{ fontSize: 40 }} />
                  <div className="mt-2" style={{ fontSize: 18, fontWeight: 600 }}>
                    {bucketItems.length === 0 ? 'Nothing to re-bucket' : 'All re-bucketed!'}
                  </div>
                  <div className="text-muted">
                    {bucketItems.length === 0
                      ? `The AI confidently bucketed every entry (≥ ${fmtHours(result?.bucketThreshold ?? 75)}%) — nothing needs review.`
                      : `You reviewed all ${bucketItems.length} description${bucketItems.length === 1 ? '' : 's'} that were Uncategorized or below the ${fmtHours(result?.bucketThreshold ?? 75)}% confidence bar.`}
                  </div>
                  <div className="mt-4 d-flex gap-2 justify-content-center">
                    {bucketItems.length > 0 ? (
                      <Button appearance="subtle" onClick={bucketBack}><i className="bi bi-arrow-left me-1" /> Back</Button>
                    ) : null}
                    <Button appearance="primary" onClick={goToResults} loading={running}>Next: Results <i className="bi bi-chevron-right ms-1" /></Button>
                  </div>
                </div>
              ) : (
                <ReviewCarousel
                  items={bucketItems}
                  index={bucketIndex}
                  decisions={bucketDecisions}
                  total={bucketItems.length}
                  hint={<>Confirm or assign a bucket (Uncategorized or low-confidence). <kbd>1</kbd>/<kbd>2</kbd>/<kbd>3</kbd> = bucket, <kbd>0</kbd> = not eligible, <kbd>⌫</kbd> = back.</>}
                  actions={[
                    ...availableBuckets.map((b, i) => ({ label: b.key, value: b.key, color: ['blue', 'green', 'violet'][i] || 'cyan', appearance: 'primary', hotkey: String(i + 1), direction: 'right' })),
                    { label: 'Not eligible', value: '__not_eligible__', color: 'red', appearance: 'ghost', hotkey: '0', direction: 'left' },
                  ]}
                  onDecide={bucketDecide}
                  onBack={bucketBack}
                  canBack={bucketIndex > 0}
                  onSkip={goToResults}
                  disabled={reviewBucket.isPending || reviewEligibility.isPending}
                  renderCard={(item) => (
                    <>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 16 }}>{taskWithParent(item.taskName, item.parentTaskName)}</div>
                          <div className="text-muted small">{item.user} · {item.date || '—'} · {fmtHours(item.hours)} h · {item.entries} entr{item.entries === 1 ? 'y' : 'ies'}</div>
                        </div>
                        {item.bucket && item.bucket !== 'Uncategorized' ? (
                          <div className="text-end">
                            <Tag color="blue">AI: {item.bucket}</Tag>
                            {item.confidence != null ? <div className="text-muted small mt-1">Low confidence · {fmtHours(item.confidence)}%</div> : null}
                          </div>
                        ) : (
                          <Tag color="orange">Uncategorized</Tag>
                        )}
                      </div>
                      <div className="mt-3 p-3" style={{ background: 'var(--dm-surface-low, #f5f5f5)', borderRadius: 8, minHeight: 96 }}>
                        <div className="text-muted small mb-1">Description</div>
                        <div style={{ fontSize: 15 }}>{item.description || <span className="text-muted">(no description)</span>}</div>
                      </div>
                    </>
                  )}
                />
              )}
            </Panel>
          ) : (
            <Message type="info" showIcon>Run the classification to review buckets.</Message>
          )
      ) : null}

      {/* Step 3 — review slush eligibility (the Step-1 gate; runs before bucketing) */}
      {step === 2 ? (
        running ? loadingPanel
          : result && result.status === 'classifier_unavailable' ? (
            <Message type="warning" showIcon>{result.message}</Message>
          ) : result && result.status === 'ok' ? (
            <Panel bordered header={reviewIndex < reviewItems.length ? `Review eligibility — ${reviewIndex + 1} of ${reviewItems.length}` : 'Review eligibility'}>
              {(() => {
                const threshold = Number.isFinite(Number(result?.reviewThreshold)) ? Number(result.reviewThreshold) : 20;
                return (
                  <div className="text-muted small mb-3">
                    Review only low-confidence AI verdicts (confidence ≤ {fmtHours(threshold)}%) — slush and full-credit alike.
                    Higher-confidence AI decisions are auto-applied. Previously decided items stay out of this queue unless you
                    disable the eligibility cache for the run.
                  </div>
                );
              })()}
              <div className="text-muted small mb-3">
                <strong>Slush</strong> you mark <em>no credit</em> is skipped before bucketing.{' '}
                <strong>Full-credit</strong> entries here still count by default (the task was pre-approved) —
                mark one <em>no credit</em> only to reject it.
              </div>
              {reviewIndex >= reviewItems.length ? (
                <div className="text-center" style={{ padding: '40px 16px' }}>
                  <i className={`bi ${reviewItems.length === 0 ? 'bi-clipboard-check' : 'bi-check-circle-fill'} text-success`} style={{ fontSize: 44 }} />
                  <div className="mt-2" style={{ fontSize: 18, fontWeight: 600 }}>
                    {reviewItems.length === 0 ? 'Nothing to review' : 'All reviewed!'}
                  </div>
                  <div className="text-muted">
                    {reviewItems.length === 0
                      ? 'No low-confidence undecided entries were found. AI decisions were auto-applied, and previously manual decisions remained cached.'
                      : `You reviewed all ${reviewItems.length} unique description${reviewItems.length === 1 ? '' : 's'}. Anything left undecided keeps the AI verdict (full-credit still counts).`}
                  </div>
                  {reviewItems.length > 0 ? (
                    <div className="text-muted small mt-2">
                      {reviewItems.filter((it) => reviewDecisions[it.reviewKey] === 'include').length} credited ·{' '}
                      {reviewItems.filter((it) => reviewDecisions[it.reviewKey] === 'exclude').length} no-credit ·{' '}
                      {reviewRemaining.length} left to the AI.
                    </div>
                  ) : null}
                  <div className="mt-4 d-flex gap-2 justify-content-center">
                    {reviewItems.length > 0 ? (
                      <Button appearance="subtle" onClick={slushBack}><i className="bi bi-arrow-left me-1" /> Back</Button>
                    ) : null}
                    <Button appearance="primary" onClick={goToBuckets} loading={running}>Next: Review buckets <i className="bi bi-chevron-right ms-1" /></Button>
                  </div>
                </div>
              ) : (
                <ReviewCarousel
                  items={reviewItems}
                  index={reviewIndex}
                  decisions={reviewDecisions}
                  total={reviewItems.length}
                  hint={<>AI applied the four-part test. <kbd>E</kbd> = credit (→), <kbd>Q</kbd> = no-credit (←), <kbd>⌫</kbd> = back.</>}
                  actions={[
                    { label: 'No credit', value: 'exclude', color: 'red', appearance: 'ghost', icon: 'bi-x-lg', hotkey: 'q', direction: 'left' },
                    { label: 'Credit', value: 'include', color: 'green', appearance: 'primary', icon: 'bi-check-lg', hotkey: 'e', direction: 'right' },
                  ]}
                  onDecide={slushDecide}
                  onBack={slushBack}
                  canBack={reviewIndex > 0}
                  onSkip={goToBuckets}
                  disabled={reviewEligibility.isPending}
                  renderCard={(item) => (
                    <>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 16 }}>{taskWithParent(item.taskName, item.parentTaskName)}</div>
                          <div className="text-muted small">{item.user} · {item.date || '—'} · {fmtHours(item.hours)} h · {item.entries} entr{item.entries === 1 ? 'y' : 'ies'}</div>
                        </div>
                        <div className="text-end">
                          {item.level === 'all' ? <Tag color="green">Full credit</Tag> : <Tag color="orange">Slush</Tag>}
                          {item.level === 'all' ? <div className="text-muted small mt-1">Counts unless rejected</div> : null}
                        </div>
                      </div>
                      <div className="mt-3 p-3" style={{ background: 'var(--dm-tint-amber, #faf7ef)', borderRadius: 8, minHeight: 80 }}>
                        <div className="text-muted small mb-1">Description</div>
                        <div style={{ fontSize: 15 }}>{item.description || <span className="text-muted">(no description)</span>}</div>
                      </div>
                      <div className="mt-3 p-3" style={{ background: item.eligible ? 'rgba(25,135,84,0.07)' : 'rgba(220,53,69,0.07)', borderRadius: 8 }}>
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-1">
                          <Tag color={item.eligible ? 'green' : 'red'}>AI: {item.eligible ? 'Eligible' : 'Not eligible'}</Tag>
                          {item.eligibilityConfidence != null ? <Tag color="blue">Confidence: {fmtHours(item.eligibilityConfidence)}%</Tag> : null}
                        </div>
                        <div className="text-muted" style={{ fontSize: 14 }}>{item.eligibilityReason || 'No reason given.'}</div>
                      </div>
                    </>
                  )}
                />
              )}
            </Panel>
          ) : (
            <Message type="info" showIcon>Screen eligibility to review.</Message>
          )
      ) : null}

      {/* Step 5 — results */}
      {step === 4 ? (
        running ? loadingPanel
          : result && result.status === 'classifier_unavailable' ? (
            <Message type="warning" showIcon>{result.message}</Message>
          ) : result && result.status === 'ok' ? (
          <Panel
            bordered
            header={(
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span>Results</span>
                <span className="d-flex gap-2">
                  <Button size="sm" appearance="ghost" onClick={() => handleExport('xlsx')} loading={running}>
                    <i className="bi bi-file-earmark-spreadsheet me-1" /> Excel
                  </Button>
                  <Button size="sm" appearance="ghost" onClick={() => handleExport('pdf')} loading={running}>
                    <i className="bi bi-file-earmark-pdf me-1" /> PDF
                  </Button>
                </span>
              </div>
            )}
          >
            {pendingRefresh > 0 ? (
              <Message type="warning" showIcon className="mb-3">
                {pendingRefresh} review decision{pendingRefresh === 1 ? '' : 's'} saved.{' '}
                {lastDecision ? (
                  <>
                    <Button appearance="link" size="xs" className="p-0" onClick={undoLastDecision} disabled={reviewEligibility.isPending}>Undo last</Button>{' · '}
                  </>
                ) : null}
                <Button appearance="link" size="xs" className="p-0" onClick={refreshFinals} loading={running}>Refresh finals</Button>{' '}
                to update totals.
              </Message>
            ) : null}

            {(() => {
              const rdHours = Number(result.totals?.hours) || 0;
              const excludedHours = Number(result.summary?.excludedTaskHours) || 0;
              const trackedHours = rdHours + excludedHours;
              const rdPct = trackedHours > 0 ? (rdHours / trackedHours) * 100 : 0;
              const creditCost = Number(result.summary?.creditCost) || 0;
              const missingRateHours = Number(result.summary?.creditCostHoursMissingRate) || 0;
              return (
                <div ref={summaryRef} className="d-flex flex-wrap gap-3 align-items-stretch mb-3">
                  <SummaryStat label="Total R&D Hours" value={`${fmtHours(rdHours)} h`} sub={`${result.summary.includedEntries} entries (incl. ${result.summary.approvedSlushEntries} approved slush)`} />
                  <SummaryStat label="Eligible Entries" value={result.summary.eligibleEntries} sub={`${result.summary.allCreditTasks} all-credit · ${result.summary.slushTasks} slush`} style={{ flex: '0 1 150px', minWidth: 140 }} />
                  <SummaryStat label="Dropped slush" value={`${fmtHours(result.summary.droppedSlushHours)} h`} sub={`${result.summary.droppedSlushEntries} entries`} style={{ flex: '0 1 150px', minWidth: 140 }} />
                  {result.summary.notEligibleSlushEntries ? (
                    <SummaryStat label="Ineligible slush" value={`${fmtHours(result.summary.notEligibleSlushHours)} h`} sub={`${result.summary.notEligibleSlushEntries} screened out`} style={{ flex: '0 1 150px', minWidth: 140 }} />
                  ) : null}
                  <SummaryStat label="Excluded tasks" value={`${fmtHours(excludedHours)} h`} sub={`${result.summary.excludedTaskEntries} entries`} style={{ flex: '0 1 200px', minWidth: 190 }} />
                  <div className="ms-auto d-flex gap-3" style={{ flex: '0 0 auto' }}>
                    <SummaryStat
                      label="R&D cost to company"
                      value={fmtMoney(creditCost)}
                      sub={missingRateHours > 0 ? `${fmtHours(missingRateHours)} h have no rate on file` : `across ${fmtHours(rdHours)} credited h`}
                      style={{ flex: '0 0 auto', minWidth: 300 }}
                    />
                    <SummaryStat
                      label="R&D % of hours"
                      value={`${fmtHours(rdPct)}%`}
                      sub={`${fmtHours(rdHours)} of ${fmtHours(trackedHours)} h`}
                      style={{ flex: '0 0 auto', minWidth: 260 }}
                    />
                  </div>
                </div>
              );
            })()}

            <div ref={chartsRef} className="d-flex flex-wrap gap-3 align-items-stretch">
              <PiePanel
                title="By Bucket"
                data={result.byBucket}
                labelKey="bucket"
                valueKey="hours"
                valueSuffix=" h"
                forceMode={exporting ? 'chart' : undefined}
                onSelect={(label) => setTableBucket((cur) => (cur === label ? null : label))}
                renderTable={() => (
                  <table className="table table-sm align-middle mb-0">
                    <thead><tr>
                      <SortHeader sort={byBucketSort} column="bucket">Bucket</SortHeader>
                      <SortHeader sort={byBucketSort} column="entries" align="end">Entries</SortHeader>
                      <SortHeader sort={byBucketSort} column="hours" align="end">Hours</SortHeader>
                      <SortHeader sort={byBucketSort} column="cost" align="end">Cost</SortHeader>
                    </tr></thead>
                    <tbody>{byBucketSort.sorted.map((b) => (<tr key={b.bucket}><td>{b.bucket}</td><td className="text-end">{b.entries}</td><td className="text-end">{fmtHours(b.hours)}</td><td className="text-end">{fmtMoney(b.cost)}</td></tr>))}</tbody>
                  </table>
                )}
              />
              <PiePanel
                title="By Employee"
                data={result.byUser}
                labelKey="user"
                valueKey="hours"
                valueSuffix=" h"
                forceMode={exporting ? 'chart' : undefined}
                onSelect={(label) => setTableEmployee((cur) => (cur === label ? null : label))}
                renderTable={() => (
                  <table className="table table-sm align-middle mb-0">
                    <thead><tr>
                      <SortHeader sort={byUserSort} column="user">Employee</SortHeader>
                      <SortHeader sort={byUserSort} column="entries" align="end">Entries</SortHeader>
                      <SortHeader sort={byUserSort} column="hours" align="end">Hours</SortHeader>
                      <SortHeader sort={byUserSort} column="cost" align="end">Cost</SortHeader>
                    </tr></thead>
                    <tbody>{byUserSort.sorted.map((u) => (<tr key={u.user}><td>{u.user}</td><td className="text-end">{u.entries}</td><td className="text-end">{fmtHours(u.hours)}</td><td className="text-end">{fmtMoney(u.cost)}</td></tr>))}</tbody>
                  </table>
                )}
              />
              <PiePanel
                title="By Task"
                data={(result.byTask || []).map((t) => ({
                  ...t,
                  taskDisplay: taskWithParent(t.taskName, t.parentTaskName),
                }))}
                labelKey="taskDisplay"
                valueKey="hours"
                valueSuffix=" h"
                forceMode={exporting ? 'chart' : undefined}
                onSelect={(label) => setTableTask((cur) => (cur === label ? null : label))}
                renderTable={() => (
                  <table className="table table-sm align-middle mb-0">
                    <thead><tr>
                      <SortHeader sort={byTaskSort} column="taskDisplay">Task</SortHeader>
                      <SortHeader sort={byTaskSort} column="level">Type</SortHeader>
                      <SortHeader sort={byTaskSort} column="hours" align="end">Hours</SortHeader>
                      <SortHeader sort={byTaskSort} column="cost" align="end">Cost</SortHeader>
                    </tr></thead>
                    <tbody>{byTaskSort.sorted.map((t) => (<tr key={t.taskId}><td>{t.taskDisplay}</td><td>{eligibilityTag(t.level)}</td><td className="text-end">{fmtHours(t.hours)}</td><td className="text-end">{fmtMoney(t.cost)}</td></tr>))}</tbody>
                  </table>
                )}
              />
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2 mt-3 p-2 rounded" style={{ background: 'rgba(0,115,116,0.05)' }}>
              <span className="text-muted small">Filter tables:</span>
              <SelectPicker
                size="sm"
                data={employeeFilterOptions}
                value={tableEmployee}
                onChange={setTableEmployee}
                placeholder="All employees"
                style={{ width: 220 }}
              />
              {tableTask ? (
                <Tag color="cyan" closable onClose={() => setTableTask(null)}>Task: {tableTask}</Tag>
              ) : null}
              {tableBucket ? (
                <Tag color="violet" closable onClose={() => setTableBucket(null)}>Bucket: {tableBucket}</Tag>
              ) : null}
              {(!tableTask && !tableBucket) ? (
                <span className="text-muted small">or click a slice in the By-Employee / By-Task / By-Bucket charts</span>
              ) : null}
              {(tableEmployee || tableTask || tableBucket) ? (
                <Button size="xs" appearance="link" onClick={() => { setTableEmployee(null); setTableTask(null); setTableBucket(null); }}>Clear filters</Button>
              ) : null}
            </div>

            <style>{`
              .rnd-scroll{max-height:440px;overflow:auto;}
              .rnd-scroll thead th{position:sticky;top:0;background:var(--dm-surface, #fff);box-shadow:inset 0 -1px 0 #dee2e6;z-index:1;}
            `}</style>

            <Panel bordered header={`Full breakdown (${rowsFilter.filtered.length} of ${result.rows.length} rows)`} className="mt-3">
              <RowFilterBar filter={rowsFilter} total={preRows.length} searchPlaceholder="Search employee / task / bucket…" />
              <div className="table-responsive rnd-scroll">
                <table className="table table-sm align-middle mb-0">
                  <thead><tr>
                    <SortHeader sort={rowsSort} column="year">Year</SortHeader>
                    <SortHeader sort={rowsSort} column="user">Employee</SortHeader>
                    <SortHeader sort={rowsSort} column="taskName">Task</SortHeader>
                    <SortHeader sort={rowsSort} column="bucket">Bucket</SortHeader>
                    <SortHeader sort={rowsSort} column="entries" align="end">Entries</SortHeader>
                    <SortHeader sort={rowsSort} column="hours" align="end">Hours</SortHeader>
                    <SortHeader sort={rowsSort} column="cost" align="end">Cost</SortHeader>
                    <SortHeader sort={rowsSort} column="avgConfidence" align="end">Avg conf.</SortHeader>
                  </tr></thead>
                  <tbody>
                    {rowsSort.sorted.map((r, i) => (
                      <tr key={`${r.year}-${r.user}-${r.taskId}-${r.bucket}-${i}`}>
                        <td>{r.year}</td><td>{r.user}</td><td>{taskWithParent(r.taskName, r.parentTaskName)}</td><td>{r.bucket}</td>
                        <td className="text-end">{r.entries}</td><td className="text-end">{fmtHours(r.hours)}</td>
                        <td className="text-end">{fmtMoney(r.cost)}</td>
                        <td className="text-end">{r.avgConfidence == null ? '—' : `${fmtHours(r.avgConfidence)}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel bordered header={`Included entries — raw data (${includedFilter.filtered.length} of ${(result.included || []).length})`} className="mt-3">
              <div className="text-muted small mb-2">
                Every individual time entry counted toward the credit total. The AI eligibility verdict is
                advisory for all-credit entries — click a row to see all TimeCamp data, or mark it
                <em> not eligible</em> to kick it out of the total (applies to every entry with that description).
              </div>
              <RowFilterBar filter={includedFilter} total={preIncluded.length} searchPlaceholder="Search employee / task / bucket / description…">
                <SelectPicker
                  size="sm"
                  data={[
                    { label: 'AI eligible', value: 'eligible' },
                    { label: 'AI not eligible', value: 'not_eligible' },
                  ]}
                  value={includedEligFilter}
                  onChange={setIncludedEligFilter}
                  placeholder="All eligibility"
                  searchable={false}
                  style={{ width: 170 }}
                />
              </RowFilterBar>
              <div className="table-responsive rnd-scroll">
                <table className="table table-sm align-middle mb-0">
                  <thead><tr>
                    <SortHeader sort={includedSort} column="year">Year</SortHeader>
                    <SortHeader sort={includedSort} column="user">Employee</SortHeader>
                    <SortHeader sort={includedSort} column="taskName">Task</SortHeader>
                    <SortHeader sort={includedSort} column="bucket">Bucket</SortHeader>
                    <SortHeader sort={includedSort} column="hours" align="end">Hours</SortHeader>
                    <SortHeader sort={includedSort} column="cost" align="end">Cost</SortHeader>
                    <SortHeader sort={includedSort} column="eligibilityConfidence">R&amp;D eligibility</SortHeader>
                    <SortHeader sort={includedSort} column="description">Description</SortHeader>
                    <th style={{ width: 96 }} />
                  </tr></thead>
                  <tbody>
                    {includedSort.sorted.map((d, i) => {
                      const denied = d.description && deniedDescriptions.has(d.description);
                      return (
                      <tr
                        key={`${d.reviewKey || d.entryId || i}-${i}`}
                        onClick={() => setDetailEntry(d)}
                        style={{ cursor: 'pointer', ...(denied ? { opacity: 0.55, textDecoration: 'line-through' } : null) }}
                      >
                        <td>{d.year}</td>
                        <td>{d.user}</td>
                        <td>{taskWithParent(d.taskName, d.parentTaskName)}</td>
                        <td>{d.bucket}</td>
                        <td className="text-end">{fmtHours(d.hours)}</td>
                        <td className="text-end">{fmtMoney(d.cost)}</td>
                        <td style={{ textDecoration: 'none' }}>{eligibilityVerdictTag(d)}</td>
                        <td>{d.description || <span className="text-muted">—</span>}</td>
                        <td onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }}>
                          {!d.description ? null : denied ? (
                            <Button size="xs" appearance="link" onClick={() => decide(d, 'reset')} disabled={reviewEligibility.isPending}>Undo</Button>
                          ) : (
                            <Button size="xs" appearance="link" color="red" onClick={() => decide(d, 'exclude')} disabled={reviewEligibility.isPending}>Not eligible</Button>
                          )}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>

            {approved.length > 0 ? (
              <Panel bordered header={`Approved slush (${approved.length})`} className="mt-3">
                <RowFilterBar filter={approvedFilter} total={approved.length} searchPlaceholder="Search description / task / employee…" />
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead><tr>
                      <SortHeader sort={approvedSort} column="date">Date</SortHeader>
                      <SortHeader sort={approvedSort} column="user">Employee</SortHeader>
                      <SortHeader sort={approvedSort} column="taskName">Task</SortHeader>
                      <SortHeader sort={approvedSort} column="description">Description</SortHeader>
                      <SortHeader sort={approvedSort} column="bucket">Bucket</SortHeader>
                      <SortHeader sort={approvedSort} column="hours" align="end">Hours</SortHeader>
                      <th style={{ width: 90 }} />
                    </tr></thead>
                    <tbody>
                      {approvedSort.sorted.map((d) => (
                        <tr key={d.reviewKey}>
                          <td style={{ whiteSpace: 'nowrap' }}>{d.date || '—'}</td>
                          <td>{d.user}</td><td>{taskWithParent(d.taskName, d.parentTaskName)}</td>
                          <td>{d.description || <span className="text-muted">—</span>}</td>
                          <td>{d.bucket}</td><td className="text-end">{fmtHours(d.hours)}</td>
                          <td><Button size="xs" appearance="link" color="red" onClick={() => decide(d, 'exclude')} disabled={reviewEligibility.isPending}>No credit</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            ) : null}

            <Panel collapsible defaultExpanded={false} bordered header={`Dropped slush — audit (${dropped.length}) — click to expand`} className="mt-3">
              {dropped.length === 0 ? (
                <div className="text-muted">No slush entries were dropped.</div>
              ) : (
                <>
                <RowFilterBar filter={droppedFilter} total={dropped.length} searchPlaceholder="Search description / task / employee…" />
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead><tr>
                      <SortHeader sort={droppedSort} column="date">Date</SortHeader>
                      <SortHeader sort={droppedSort} column="user">Employee</SortHeader>
                      <SortHeader sort={droppedSort} column="taskName">Task</SortHeader>
                      <SortHeader sort={droppedSort} column="description">Description</SortHeader>
                      <SortHeader sort={droppedSort} column="bucket">Bucket</SortHeader>
                      <SortHeader sort={droppedSort} column="confidence" align="end">Conf.</SortHeader>
                      <SortHeader sort={droppedSort} column="hours" align="end">Hours</SortHeader>
                      <SortHeader sort={droppedSort} column="reason">Reason</SortHeader>
                      <th style={{ width: 120 }} />
                    </tr></thead>
                    <tbody>
                      {droppedSort.sorted.map((d) => (
                        <tr key={d.reviewKey}>
                          <td style={{ whiteSpace: 'nowrap' }}>{d.date || '—'}</td>
                          <td>{d.user}</td><td>{taskWithParent(d.taskName, d.parentTaskName)}</td>
                          <td>{d.description || <span className="text-muted">—</span>}</td>
                          <td>{d.bucket}</td>
                          <td className="text-end">{d.confidence == null ? '—' : `${fmtHours(d.confidence)}%`}</td>
                          <td className="text-end">{fmtHours(d.hours)}</td>
                          <td>
                            {d.reason === 'reviewed_excluded' ? <Tag color="red" size="sm">Rejected</Tag>
                              : d.reason === 'not_eligible' ? <Tag color="violet" size="sm" title={d.note || 'AI screened this out as non-qualifying R&D.'}>Not eligible</Tag>
                              : d.reason === 'low_confidence' ? <Tag color="orange" size="sm">Low conf.</Tag>
                              : <Tag size="sm">Non-R&amp;D</Tag>}
                          </td>
                          <td>
                            {d.reason === 'reviewed_excluded' ? (
                              <Button size="xs" appearance="link" onClick={() => decide(d, 'reset')} disabled={reviewEligibility.isPending}>Undo</Button>
                            ) : (
                              <Button size="xs" appearance="link" onClick={() => creditAnyway(d)} disabled={reviewEligibility.isPending}>Credit anyway</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </Panel>
          </Panel>
        ) : (
          <Message type="info" showIcon>Run the classification to see results.</Message>
        )
      ) : null}

      {/* Wizard navigation */}
      <div className="d-flex justify-content-between">
        <Button appearance="subtle" onClick={goPrev} disabled={step === 0 || running}>
          <i className="bi bi-chevron-left me-1" /> Prev
        </Button>
        {step === 0 ? (
          <Button appearance="primary" onClick={handlePullTasks} disabled={!rangeValid} loading={pullTasks.isPending}>
            Next: Pull tasks <i className="bi bi-chevron-right ms-1" />
          </Button>
        ) : step === 1 ? (
          <Button appearance="primary" onClick={handleRun} loading={running || updateSettings.isPending}>
            Next: Screen eligibility <i className="bi bi-chevron-right ms-1" />
          </Button>
        ) : step === 2 ? (
          <Button appearance="primary" onClick={goToBuckets} disabled={running} loading={running}>
            Next: Review buckets <i className="bi bi-chevron-right ms-1" />
          </Button>
        ) : step === 3 ? (
          <Button appearance="primary" onClick={goToResults} disabled={running} loading={running}>
            Next: Results <i className="bi bi-chevron-right ms-1" />
          </Button>
        ) : (
          <span />
        )}
      </div>
    </>
  );

  return (
    <div className="bdc-page">
      {view === 'home' ? homeView : wizardView}

      {/* Floating log widget (chatbot-style, bottom-center) */}
      {view === 'wizard' && combinedLogs.length > 0 ? (
        <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 20, zIndex: 1080 }}>
          {logOpen ? (
            <div
              style={{
                width: 380, maxWidth: 'calc(100vw - 40px)', height: 420, maxHeight: 'calc(100vh - 120px)',
                background: 'var(--dm-surface, #fff)', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid var(--dm-border, #e0e0e0)',
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between"
                style={{ background: STEP_ACCENT, color: '#fff', padding: '10px 14px' }}
              >
                <span style={{ fontWeight: 600 }}>
                  <i className="bi bi-terminal me-2" />Run log
                  <span style={{ opacity: 0.8, fontWeight: 400 }}> · {combinedLogs.length}</span>
                </span>
                <button
                  type="button"
                  aria-label="Close log"
                  onClick={() => setLogOpen(false)}
                  style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
              <pre
                ref={logBodyRef}
                style={{ flex: 1, overflow: 'auto', margin: 0, padding: 12, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', background: 'var(--dm-surface, #fafafa)' }}
              >
                {combinedLogs.join('\n')}
              </pre>
            </div>
          ) : (
            <Button
              appearance="primary"
              onClick={() => setLogOpen(true)}
              style={{ borderRadius: 24, boxShadow: '0 6px 18px rgba(0,0,0,0.2)', backgroundColor: STEP_ACCENT, borderColor: STEP_ACCENT }}
            >
              <i className="bi bi-terminal me-2" />
              View log
              {running ? <span className="spinner-border spinner-border-sm ms-2" role="status" /> : <span className="ms-2" style={{ opacity: 0.85 }}>{combinedLogs.length}</span>}
            </Button>
          )}
        </div>
      ) : null}

      {/* Delete-run confirmation */}
      <ConfirmModal
        open={!!runToDelete}
        title="Delete saved run"
        body={(
          <>
            Delete <strong>{runToDelete?.name || (runToDelete ? formatRange(runToDelete.period_start, runToDelete.period_end) : 'this run')}</strong>? This can&rsquo;t be undone.
          </>
        )}
        confirmText="Delete"
        danger
        loading={deleteRun.isPending}
        onConfirm={confirmDeleteRun}
        onCancel={() => setRunToDelete(null)}
      />

      {/* Rename-run dialog */}
      <Modal open={!!renameTarget} onClose={() => setRenameTarget(null)} size="xs">
        <Modal.Header>
          <Modal.Title>Name run</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>Give this run a memorable name. The date range stays on the card regardless.</div>
          <Input
            value={renameValue}
            onChange={setRenameValue}
            placeholder="e.g. Q2 2026 R&D — Engineering"
            onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(); }}
          />
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setRenameTarget(null)} disabled={updateRun.isPending}>Cancel</NsiButton>
          <NsiButton variant="primary" onClick={confirmRename} loading={updateRun.isPending}>Save</NsiButton>
        </Modal.Footer>
      </Modal>

      {/* Entry audit modal — all TimeCamp data for one included entry + deny/undo */}
      <Modal open={!!detailEntry} onClose={() => setDetailEntry(null)} size="sm">
        <Modal.Header>
          <Modal.Title>Entry detail</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailShown ? (() => {
            const d = detailShown;
            const denied = d.description && deniedDescriptions.has(d.description);
            const sameDesc = (result?.included || []).filter((x) => x.description === d.description).length;
            const rows = [
              ['Date entered', d.date || '—'],
              ['Employee', d.user],
              ['Email', d.email || '—'],
              ['TimeCamp user id', d.timeCampUserId || '—'],
              ['TimeCamp entry id', d.entryId || '—'],
              ['Task', taskWithParent(d.taskName, d.parentTaskName)],
              ['Task id', d.taskId || '—'],
              ['Breadcrumb', d.breadcrumb || '—'],
              ['Hours', `${fmtHours(d.hours)} h`],
              ['Billable', d.billable ? 'Yes' : 'No'],
              ['Rate', d.rate == null ? '—' : `${fmtMoney(d.rate)}/h`],
              ['Cost', fmtMoney(d.cost)],
              ['Eligibility', d.level],
              ['Bucket', d.bucket],
              ['Bucket confidence', d.confidence == null ? '—' : `${fmtHours(d.confidence)}%`],
            ];
            return (
              <>
                <table className="table table-sm mb-3">
                  <tbody>
                    {rows.map(([label, value]) => (
                      <tr key={label}>
                        <td className="text-muted" style={{ width: 150, whiteSpace: 'nowrap' }}>{label}</td>
                        <td style={{ wordBreak: 'break-word' }}>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mb-2 text-muted small">Description</div>
                <div className="p-3 mb-3" style={{ background: 'var(--dm-surface-low, #f5f5f5)', borderRadius: 8 }}>
                  {d.description || <span className="text-muted">(no description)</span>}
                </div>
                <div className="p-3" style={{ background: d.eligible ? 'rgba(25,135,84,0.07)' : 'rgba(220,53,69,0.07)', borderRadius: 8 }}>
                  <div className="mb-1">{eligibilityVerdictTag(d)}</div>
                  <div className="text-muted" style={{ fontSize: 14 }}>{d.eligibilityReason || 'No reason given.'}</div>
                  {d.level === 'all' && d.eligibilitySource !== 'human' ? (
                    <div className="text-muted small mt-2"><i className="bi bi-info-circle me-1" />Advisory only — all-credit entries count unless you mark them not eligible.</div>
                  ) : null}
                </div>
                {denied ? (
                  <Message type="warning" showIcon className="mt-3">Marked not eligible. Refresh finals to update totals.</Message>
                ) : null}
                {sameDesc > 1 ? (
                  <div className="text-muted small mt-2">This decision applies to all {sameDesc} included entries with this description.</div>
                ) : null}
              </>
            );
          })() : null}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setDetailEntry(null)}>Close</NsiButton>
          {detailShown?.description ? (
            deniedDescriptions.has(detailShown.description) ? (
              <NsiButton variant="primary" onClick={() => decide(detailShown, 'reset')} disabled={reviewEligibility.isPending}>Undo — keep credit</NsiButton>
            ) : (
              <NsiButton variant="danger-solid" onClick={() => decide(detailShown, 'exclude')} disabled={reviewEligibility.isPending}>Mark not eligible</NsiButton>
            )
          ) : null}
        </Modal.Footer>
      </Modal>

      {/* Config modal — counted buckets + editable AI descriptions */}
      <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} size="md">
        <Modal.Header>
          <Modal.Title>R&amp;D Credit Config</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-3" style={{ fontSize: 13 }}>
            Check the buckets that count toward R&amp;D credit, and edit the descriptions the AI uses to classify entries.
            Editing a description re-classifies affected entries on the next run.
          </div>
          {settingsQuery.isError ? (
            <Message type="error" showIcon>
              Could not load config: {String(settingsQuery.error?.message || settingsQuery.error)}
            </Message>
          ) : (
            availableBuckets.map((bucket) => (
              <div key={bucket.key} className="mb-3 pb-3 border-bottom">
                <Checkbox
                  checked={draftCounted.includes(bucket.key)}
                  onChange={(_v, checked) =>
                    setDraftCounted((prev) => (checked ? [...new Set([...prev, bucket.key])] : prev.filter((k) => k !== bucket.key)))
                  }
                >
                  <span className="fw-semibold">{bucket.key}</span>
                  <span className="text-muted small ms-2">{draftCounted.includes(bucket.key) ? 'Counts toward credit' : 'Not counted'}</span>
                </Checkbox>
                <Input
                  as="textarea"
                  rows={3}
                  className="mt-1"
                  value={draftDescriptions[bucket.key] ?? bucket.description}
                  onChange={(val) => setDraftDescriptions((prev) => ({ ...prev, [bucket.key]: val }))}
                />
              </div>
            ))
          )}

          <div className="mt-4 p-3 rounded" style={{ border: '1px solid var(--dm-border, #e0e0e0)', background: 'var(--dm-surface, #fafafa)' }}>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
              <div className="fw-semibold"><i className="bi bi-funnel me-1" /> Eligibility prompt <span className="text-muted small">(Step 1 — slush only)</span></div>
              <NsiButton
                size="sm"
                icon="arrow-counterclockwise"
                onClick={() => setDraftEligPrompt(defaultEligibilityPrompt)}
                disabled={!defaultEligibilityPrompt || draftEligPrompt.trim() === defaultEligibilityPrompt.trim()}
              >
                Reset to default
              </NsiButton>
            </div>
            <div className="text-muted small mb-2">
              The gate the AI applies to <strong>slush</strong> entries to decide whether the work qualifies as R&amp;D
              (the four-part test) before it&rsquo;s bucketed. All-credit tasks skip this. Editing it re-screens slush entries on the next run.
            </div>
            <Input
              as="textarea"
              rows={10}
              value={draftEligPrompt}
              onChange={setDraftEligPrompt}
              placeholder={defaultEligibilityPrompt}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>

          <div className="mt-4 p-3 rounded" style={{ border: '1px solid var(--dm-border, #e0e0e0)', background: 'var(--dm-surface, #fafafa)' }}>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
              <div className="fw-semibold"><i className="bi bi-robot me-1" /> Bucket prompt <span className="text-muted small">(Step 2)</span></div>
              <NsiButton
                size="sm"
                icon="arrow-counterclockwise"
                onClick={() => setDraftBucketPrompt(defaultBucketPrompt)}
                disabled={!defaultBucketPrompt || draftBucketPrompt.trim() === defaultBucketPrompt.trim()}
              >
                Reset to default
              </NsiButton>
            </div>
            <div className="text-muted small mb-2">
              Sorts each eligible entry into a work-type bucket and rates its confidence (the confidence drives which
              slush entries auto-qualify for review). Use the <code>{'{{buckets}}'}</code>{' '}
              placeholder where the bucket list above should be inserted. Editing this re-classifies affected entries on the next run.
            </div>
            <Input
              as="textarea"
              rows={10}
              value={draftBucketPrompt}
              onChange={setDraftBucketPrompt}
              placeholder={defaultBucketPrompt}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            {draftBucketPrompt.trim() && !draftBucketPrompt.includes('{{buckets}}') ? (
              <div className="text-warning small mt-1">
                <i className="bi bi-exclamation-triangle me-1" />
                No <code>{'{{buckets}}'}</code> placeholder — the bucket definitions will be appended to the end of your prompt.
              </div>
            ) : null}
          </div>

          <div className="mt-4 p-3 rounded" style={{ border: '1px solid var(--dm-border, #e0e0e0)', background: 'var(--dm-surface, #fafafa)' }}>
            <div className="fw-semibold mb-1"><i className="bi bi-database me-1" /> Caches</div>
            <div className="text-muted small mb-3">
              Uncheck a cache to redo that work from scratch on the next run — the cache is kept and re-saved afterward.
              Use <span className="text-danger">Clear</span> to delete it permanently (this cannot be undone).
            </div>
            <div className="d-flex flex-column gap-2">
              {CACHE_KEYS.map((key) => (
                <div key={key} className="d-flex align-items-center justify-content-between gap-2">
                  <Checkbox
                    checked={caches[key]}
                    onChange={(_v, checked) => setCaches((c) => ({ ...c, [key]: checked }))}
                  >
                    <span className="small fw-semibold">{CACHE_META[key].label}</span>
                    <span className="text-muted small ms-2">{caches[key] ? 'Used on runs' : 'Skipped — redone each run'}</span>
                  </Checkbox>
                  <NsiButton size="sm" variant="danger" icon="trash" onClick={() => setCacheToClear(key)}>
                    Clear
                  </NsiButton>
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setSettingsOpen(false)} disabled={updateSettings.isPending}>Cancel</NsiButton>
          <NsiButton variant="primary" onClick={saveSettings} loading={updateSettings.isPending} disabled={settingsQuery.isLoading || settingsQuery.isError}>Save</NsiButton>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        open={!!cacheToClear}
        title={<><i className="bi bi-exclamation-triangle-fill text-danger me-2" />Clear {cacheToClear ? CACHE_META[cacheToClear].label : 'cache'}?</>}
        body={(
          <>
            <Message type="error" showIcon className="mb-2">This action is permanent and cannot be undone.</Message>
            {cacheToClear ? <p className="mb-0">{CACHE_META[cacheToClear].warn}</p> : null}
          </>
        )}
        confirmText="Clear permanently"
        danger
        backdrop="static"
        loading={clearCache.isPending}
        onConfirm={confirmClearCache}
        onCancel={() => setCacheToClear(null)}
      />
    </div>
  );
}
